
import moment from "moment";
import React, { useReducer, useRef } from "react";
import { Box, Card, Flex, Text } from "rebass/styled-components"
import { AreaChart, Area, XAxis, Tooltip, ResponsiveContainer, TooltipProps } from 'recharts';
import { NameType, ValueType } from "recharts/types/component/DefaultTooltipContent";
import styled from "styled-components";
import { Skeleton } from "../../../components/loadingSkeleton/Skeleton";
import { sleep } from "../../../utils/sleep";
import { IOptionsDaysBack } from "../../Patient";
import { IFlattenCategory } from "../Symptoms";


const HoverableCard = styled(Card)`
  transition:all 0.3s ease;
`
const ToolTip = styled(Box)`
  padding: 0.8rem;
  background: #4E658A;
border-radius: 10px;
  box-shadow: 3px 3px 3px rgba(0, 0, 0, 0.3);
`

const ChartScrollButtons = styled(Box)`
  position: absolute;
  opacity: 0.5;
  top: calc(50% - 30px);
   background: #FFFFFF;
   border-radius: 14px;
   width: 38px;
   height: 38px;
   display: flex;
   justify-content: center;
   align-items: center;
   border: 1px solid #344F;
  transition:all 0.3s ease;

   &:hover {
     opacity: 1;
   }
`

const CustomizedTooltip = ({ active, payload, label }: TooltipProps<ValueType, NameType>) => {
  if (active) {
    return (
      <ToolTip>
        <Flex flexDirection={'column'}>
          <Text fontWeight={300} color='white'>{!!payload && payload.length > 0 ? payload[0].payload.date : ''}</Text>
          <Text fontWeight={500} color='white'>{!!payload && payload.length > 0 && (!!payload[0].payload.values || payload[0].payload.values === 0) ? payload[0].payload.values.toFixed(2) : ''}</Text>
        </Flex>

      </ToolTip>
    );
  }

  return null;
};

const getColorsByCategory = (category: string) => {
  switch (category.toLowerCase()) {
    case 'global':
      return {
        bg: '#B8D6F5',
        bg2: '#B8D6F5',
        op1: 1,
        op2: 0,
        line: '#344F7A'
      }

    case 'mind':
      return {
        bg: '#70FCD2',
        bg2: '#BCFFEB',
        op1: 0.29,
        op2: 0.0,
        line: '#15D89D'
      }

    case 'body':
      return {
        bg: '#F8A294',
        bg2: '#FFE3D8',
        op1: 0.46,
        op2: 0.0,
        line: '#F8A294'
      }

    default:
      return {
        bg: '#B8D6F5',
        bg2: '#B8D6F5',
        op1: 1,
        op2: 0.0,
        line: '#344F7A'
      }
  }
}

const SymptomsChart = (
  {
    expandedItem,
    setExpandedItem,
    item,
    isExpansion,
    selectedPeriod
  }: {
    expandedItem: string | null | undefined,
    setExpandedItem: (x: string | null | undefined) => void,
    item: IFlattenCategory,
    isExpansion?: boolean,
    selectedPeriod: IOptionsDaysBack
  }) => {
  const refBox = useRef<any>(null)
  const intervalRef = React.useRef<any>(null);
  const [, forceupdate] = useReducer((x: any) => x + 1, 0)

  const scrollWidth = refBox.current ? refBox.current.scrollWidth : null
  const scrollLeft = refBox.current ? refBox.current.scrollLeft : null
  const clientWidth = refBox.current ? refBox.current.clientWidth : null

  const scroll = (scrollOffset: number) => {
    if (refBox && refBox.current)
      if (scrollWidth && scrollLeft && clientWidth && scrollLeft + clientWidth >= scrollWidth && scrollOffset > 0) {
        stopCounter()
        return
      } else if (scrollOffset < 0 && scrollLeft <= 0) {
        stopCounter()
        return
      }
    refBox.current.scrollLeft += scrollOffset;
  };

  React.useEffect(() => {
    async function scrollToRight() {
      await sleep(300)
      if (refBox.current) {
        refBox.current.scrollLeft = refBox.current.scrollWidth - refBox.current.clientWidth
      }
      forceupdate()
    }

    scrollToRight()
    return () => stopCounter(); // when App is unmounted we should stop counter
  }, []);

  React.useEffect(() => {
    async function scrollToRight() {
      await sleep(300)
      if (refBox.current) {
        refBox.current.scrollLeft = refBox.current.scrollWidth - refBox.current.clientWidth
      }
      forceupdate()
    }

    scrollToRight()
  }, [item]);

  const colors = getColorsByCategory(item.category)

  const startCounter = (orientation: 'LEFT' | 'RIGHT') => {
    if (intervalRef.current) return;
    intervalRef.current = setInterval(() => {
      scroll(orientation === 'LEFT' ? -20 : 20)
    }, 10);
  };

  const stopCounter = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
      forceupdate()
    }
  };
  const interval =
    selectedPeriod.value === 90 || selectedPeriod.value === 365
      ? 2
      : 0
  let fac = 7

  if (item && item.values) {
    fac = (item.values || []).length / Math.min(selectedPeriod.value, item.values.length)
  }

  return (
    <HoverableCard
    height={['195px','235px','235px']}
    marginRight={['8px', '12px', '12px']}
      style={{
        borderRadius: '30px',
        marginBottom: '12px',
        flex: 1,
        flexBasis: isExpansion ? '100%' : 'calc(33% - 12px)',
        flexGrow: isExpansion ? 1 : 0,
        overflow: 'hidden',
        opacity: expandedItem === item.id && !isExpansion ? 0 : 1,
        width: isExpansion ? '99%' : '',
      }}>
      <Flex flex={1} flexDirection='column' height={'100%'} style={{ maxWidth: '100%', position: 'relative' }}>
        <Flex>
          <Text flex={1} fontWeight={600} fontSize='14px' m={'18px 12px'}>{item.name}</Text>
          {!isExpansion ? (
            <svg
              onClick={() => {
                if (isExpansion) {
                  setExpandedItem(null)
                  return
                }
                setExpandedItem(item.id)
              }} style={{ margin: '18px', cursor: 'pointer' }} width="12" height="12" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M2 9H0V18H9V16H2V9ZM9 2H16V9H18V0H9V2Z" fill="black" />
            </svg>
          ) : (
            <svg
              onClick={() => {
                if (isExpansion) {
                  setExpandedItem(null)
                  return
                }
                setExpandedItem(item.id)
              }} style={{ margin: '18px', cursor: 'pointer' }} width="12" height="12" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8 12H2M12 8L19 1L12 8ZM12 8H18H12ZM12 8V2V8ZM1 19L8 12L1 19ZM8 12V18V12Z" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            </svg>

          )}
        </Flex>
        <Box ref={refBox} className="hideScrollbar" style={{ maxWidth: '100%', overflow: 'scroll', position: 'relative' }} flex={1}>
          <Box style={{ width: !isExpansion ? '100%' : `calc(100% * ${fac})`, height: '100%' }}>
            <ResponsiveContainer width={'100%'} height="100%">
              <AreaChart
                data={(item.values || [] as IFlattenCategory[]).filter(x => {
                  if (!!isExpansion) return true
                  return moment(x.date).isAfter(moment().add(selectedPeriod.value * -1, 'day'))
                })}
                margin={!isExpansion ? {
                  top: 10,
                  right: 0,
                  left: 0,
                  bottom: 0,
                } : {
                  top: 10,
                  right: 30,
                  left: 30,
                  bottom: 0,
                }}
              >
                <defs>
                  <linearGradient id={`colorUv${item.id}`} x1="0.5" y1="0" x2="0.5" y2="1">
                    <stop offset="5%" stopColor={colors.bg} stopOpacity={colors.op1} />
                    <stop offset="95%" stopColor={colors.bg2} stopOpacity={colors.op2} />
                    {/* <stop offset="100%" stopColor={colors.bg2} stopOpacity={colors.op2} />
                    <stop offset="50%" stopColor={colors.bg} stopOpacity={colors.op1} /> */}
                  </linearGradient>
                </defs>
                {isExpansion && (
                  <>
                    <XAxis xAxisId={0} dy={0} dx={-0} label={{ value: '', angle: 0, position: 'bottom' }} dataKey="day" interval={interval} tickLine={false} tick={{ fontSize: 9, fontWeight: 300 }} />
                    <XAxis xAxisId={1} dy={-15} strokeOpacity={0} dx={0} label={{ value: '', angle: 0, position: 'bottom' }} interval={0} dataKey="month" tickLine={false} tick={{ fontSize: 9, fontWeight: 500 }} />
                  </>)
                }
                <Tooltip content={<CustomizedTooltip />} />
                <Area type="monotone" dataKey="values" stroke={colors.line} fillOpacity={1} fill={`url(#colorUv${item.id})`} />
              </AreaChart>
            </ResponsiveContainer>
          </Box>

        </Box>
        {!!isExpansion && (
          <>
            <ChartScrollButtons
              style={{ left: '40px', display: scrollWidth && (scrollLeft !== null && scrollLeft !== undefined) && clientWidth && scrollLeft <= 0 ? 'none' : '' }}
              onMouseDown={() => startCounter('LEFT')}
              onMouseUp={stopCounter}
              onMouseLeave={stopCounter}>
              <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M16.1405 21.6667L16.1329 21.6667C16.0333 21.667 15.9349 21.645 15.8449 21.6023L15.6306 22.0541L15.8449 21.6023C15.7552 21.5598 15.6761 21.4977 15.6134 21.4206C15.6132 21.4204 15.6131 21.4202 15.6129 21.42L9.9791 14.4215L9.97912 14.4215L9.97588 14.4175C9.87782 14.2982 9.82422 14.1486 9.82422 13.9942C9.82422 13.8402 9.87749 13.6911 9.97496 13.572L15.8071 6.57343L15.8075 6.57294C15.9207 6.43667 16.0835 6.35097 16.2599 6.3347C16.4364 6.31844 16.6121 6.37293 16.7484 6.48619L17.0639 6.10654L16.7484 6.48619C16.8846 6.59946 16.9703 6.76222 16.9866 6.93867L17.4845 6.89276L16.9866 6.93867C17.0028 7.11497 16.9485 7.29052 16.8354 7.42675C16.8353 7.42685 16.8352 7.42696 16.8351 7.42707L11.6206 13.6798L11.3581 13.9946L11.6153 14.3138L16.6553 20.5671L16.6553 20.5671L16.6605 20.5734C16.742 20.6712 16.7938 20.7904 16.8097 20.9168C16.8256 21.0431 16.805 21.1714 16.7503 21.2864C16.6956 21.4014 16.6091 21.4984 16.501 21.5658C16.3929 21.6332 16.2678 21.6682 16.1405 21.6667L16.1405 21.6667Z" fill="black" stroke="#344F7A" />
              </svg>

            </ChartScrollButtons>
            <ChartScrollButtons
              style={{ right: '40px', display: scrollWidth && scrollLeft && clientWidth && scrollLeft + clientWidth >= scrollWidth ? 'none' : '' }}
              onMouseDown={() => startCounter('RIGHT')}
              onMouseUp={stopCounter}
              onMouseLeave={stopCounter}>
              <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M11.8595 6.33329L11.8595 6.33335L11.8671 6.33332C11.9667 6.33299 12.0651 6.35497 12.1551 6.39765C12.2448 6.4402 12.3238 6.50225 12.3865 6.57927C12.3867 6.57951 12.3869 6.57975 12.3871 6.57999L18.0209 13.5785L18.0209 13.5785L18.0241 13.5825C18.1222 13.7018 18.1758 13.8514 18.1758 14.0058C18.1758 14.1598 18.1225 14.3089 18.0251 14.428L12.1929 21.4266L12.1925 21.4271C12.0793 21.5633 11.9165 21.649 11.7401 21.6653C11.5636 21.6816 11.3879 21.6271 11.2516 21.5138C11.1154 21.4005 11.0297 21.2378 11.0134 21.0613C10.9972 20.885 11.0516 20.7095 11.1647 20.5732C11.1647 20.5731 11.1648 20.573 11.1649 20.5729L16.3794 14.3202L16.6419 14.0054L16.3847 13.6862L11.3447 7.4329L11.3447 7.43286L11.3395 7.42662C11.258 7.32877 11.2062 7.20961 11.1903 7.08324C11.1744 6.95688 11.195 6.82859 11.2497 6.71358C11.3044 6.59856 11.3909 6.50162 11.499 6.43422C11.6071 6.36683 11.7322 6.3318 11.8595 6.33329Z" fill="black" stroke="#344F7A" />
              </svg>

            </ChartScrollButtons>
          </>
        )}
      </Flex>
    </HoverableCard>
  )
}
export const SymptomsChartLoading = () => {
  const randomSize = [55, 99, 147][Math.floor(Math.random() * 3)]
  return (
    <HoverableCard
      height={['195px','235px','235px']}
      marginRight={['8px', '12px', '12px']}
      style={{
        borderRadius: '30px',
        marginBottom: '12px',
        flex: 1,
        flexBasis: 'calc(33% - 12px)',
        flexGrow: 0,
        overflow: 'hidden',
        opacity: 1,
      }}>
      <Flex flex={1} flexDirection='column' height={'100%'} style={{ maxWidth: '100%', position: 'relative' }}>
        <Flex>
          <Text flex={1} fontWeight={600} fontSize='14px' m={'18px 12px'}><Skeleton width={randomSize} /></Text>

        </Flex>
        <Box className="hideScrollbar" style={{ maxWidth: '100%', overflow: 'scroll', position: 'relative', padding: '0 8px 12px 8px' }} flex={1}>
          <Box style={{ width: '100%', height: '100%' }}>
            <Skeleton width={'100%'} height='100%' style={{ borderRadius: '0 0 30px 30px' }} />
          </Box>

        </Box>
      </Flex>
    </HoverableCard>
  )
}

export default SymptomsChart